import type { ContextMenuItem } from '../../components/ContextMenu';
import type { EntityMenuContext } from '../entities/EntityKindHandler';
import type { CanvasEntity, SegmentType, ArrowMarkerType, StrokeStyle } from '../entityTypes';
import { getConnectorData, DEFAULT_EDGE_COLOR, DEFAULT_EDGE_WIDTH } from '../entityTypes';
import { Boxes, Trash2, Edit3 } from 'lucide-react';
import { EDGE_COLORS, STROKE_STYLES, STROKE_WIDTHS, ARROW_TYPES, BORDER_COLOR_DEFAULTS } from './edgeStyleConstants';
import { canvasSelectionStore } from '../stores/canvasSelectionStore';
import type { CanvasStore } from '../stores/canvasStore';
import i18n from '../../i18n';

const t = (key: string, opts?: Record<string, unknown>) => i18n.t(key as any, opts);

// ============================================
// Shared: Color Swatch Grid Builder
// ============================================

/**
 * Builds a context menu item with a compact color swatch grid submenu.
 * Shows only handler-specific colors + "Andere..." for custom hex input.
 */
export function buildColorGridSubmenu(opts: {
  id: string;
  label: string;
  separator?: boolean;
  currentColor: string | undefined;
  handlerColors: Array<{ id: string; label: string; color: string }>;
  onSelectColor: (color: string) => void;
  enableAlpha?: boolean;
}): ContextMenuItem {
  const { id, label, separator, currentColor, handlerColors, onSelectColor, enableAlpha } = opts;

  const seen = new Set<string>();
  const children: ContextMenuItem[] = [];

  for (const c of handlerColors) {
    const key = c.color.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    children.push({
      id: `${id}-${c.id}`,
      label: currentColor === c.color ? '\u25CF' : c.label,
      swatchColor: c.color,
      onClick: () => onSelectColor(c.color),
    });
  }

  // "Palette..." opens inline Sketch color picker
  children.push({
    id: `${id}-custom`,
    label: t('canvasMenu.palette'),
    colorPickerConfig: {
      currentColor: currentColor || '#374151',
      onSelectColor,
      enableAlpha,
    },
  });

  return {
    id,
    label,
    separator,
    renderChildrenAsGrid: true,
    swatchColor: currentColor,
    children,
  };
}

// ============================================
// Shared: Border Color Submenu (compact grid + custom color + remove)
// ============================================

/**
 * Builds a compact border color submenu: default colors as swatch grid,
 * a "Palette..." color picker, and a "Kein Rahmen" option.
 */
export function buildBorderColorSubmenu(opts: {
  id: string;
  label: string;
  separator?: boolean;
  currentColor: string | undefined;
  onSelectColor: (color: string) => void;
  onRemoveBorder: () => void;
}): ContextMenuItem {
  const { id, label, separator, currentColor, onSelectColor, onRemoveBorder } = opts;

  const colors = BORDER_COLOR_DEFAULTS.filter(c => c.color);
  const children: ContextMenuItem[] = [];

  for (const c of colors) {
    children.push({
      id: `${id}-${c.id}`,
      label: currentColor === c.color ? '\u25CF' : c.label,
      swatchColor: c.color,
      onClick: () => onSelectColor(c.color),
    });
  }

  // "Palette..." opens inline Sketch color picker
  children.push({
    id: `${id}-custom`,
    label: t('canvasMenu.palette'),
    colorPickerConfig: {
      currentColor: currentColor || '#374151',
      onSelectColor,
    },
  });

  // "Kein Rahmen" to remove border
  children.push({
    id: `${id}-none`,
    label: t('canvasMenu.noBorder'),
    separator: true,
    onClick: onRemoveBorder,
  });

  return {
    id,
    label,
    separator,
    renderChildrenAsGrid: true,
    swatchColor: currentColor,
    children,
  };
}

// ============================================
// Group Submenu
// ============================================

export function buildGroupSubmenu(ctx: EntityMenuContext): ContextMenuItem {
  const { store, entity, groups, selectedEntityIds, onCreateGroup, setGroupPickerState } = ctx;
  const entityId = entity.id;
  const targetIds = selectedEntityIds.includes(entityId) ? selectedEntityIds : [entityId];
  const targetSet = new Set(targetIds);
  const parentGroups = groups.filter(g => g.memberNodeIds.some(id => targetSet.has(id)));
  const groupChildren: ContextMenuItem[] = [];

  for (const g of parentGroups) {
    groupChildren.push({
      id: `group-remove-${g.id}`,
      label: `\u25CF ${g.label}`,
      onClick: () => {
        store.send({
          type: 'group.setMembers',
          groupId: g.id,
          memberEntityIds: [...g.memberNodeIds.filter(id => !targetSet.has(id)), ...g.memberGroupIds],
        });
      },
    });
  }

  if (parentGroups.length > 0) {
    groupChildren.push({
      id: 'group-remove-all',
      label: t('canvasMenu.removeFromAllGroups'),
      separator: true,
      onClick: () => {
        for (const g of groups) {
          const remaining = g.memberNodeIds.filter(id => !targetSet.has(id));
          if (remaining.length < g.memberNodeIds.length) {
            store.send({
              type: 'group.setMembers',
              groupId: g.id,
              memberEntityIds: [...remaining, ...g.memberGroupIds],
            });
          }
        }
      },
    });
  }

  if (onCreateGroup) {
    groupChildren.push({
      id: 'group-create',
      label: t('canvasMenu.group'),
      separator: parentGroups.length === 0,
      onClick: () => {
        if (targetIds.length > 1) {
          canvasSelectionStore.send({ type: 'entity.set', entityIds: targetIds });
        } else {
          canvasSelectionStore.send({ type: 'entity.set', entityIds: [entityId] });
        }
        onCreateGroup();
      },
    });
  }

  if (setGroupPickerState) {
    groupChildren.push({
      id: 'group-assign-modal',
      label: t('canvasMenu.assignGroup'),
      onClick: () => setGroupPickerState({ targetNodeIds: targetIds }),
    });
  }

  return {
    id: 'group-submenu',
    label: t('canvasMenu.groupLabel'),
    icon: Boxes,
    iconColor: '#7c3aed',
    children: groupChildren,
  };
}

// ============================================
// Multi-Delete Item
// ============================================

export function buildMultiDeleteItem(
  ctx: EntityMenuContext,
  label: string,
): ContextMenuItem | null {
  const { store, entity, selectedEntityIds } = ctx;
  const entityId = entity.id;

  if (selectedEntityIds.length < 2 || !selectedEntityIds.includes(entityId)) {
    return null;
  }

  return {
    id: 'remove-selected',
    label: t('canvasMenu.removeCount', { count: selectedEntityIds.length, label }),
    icon: Trash2,
    iconColor: '#dc2626',
    onClick: () => {
      if (ctx.onRemoveSelected) {
        ctx.onRemoveSelected();
      } else {
        for (const id of selectedEntityIds) {
          store.send({ type: 'entity.remove', entityId: id });
        }
        canvasSelectionStore.send({ type: 'entity.clear' });
      }
    },
  };
}

// ============================================
// Connector Style Submenus (low-level, shared)
// ============================================

export interface ConnectorStyleData {
  segmentType: SegmentType;
  strokeColor?: string;
  strokeWidth?: number;
  strokeStyle?: StrokeStyle;
  arrowStart?: ArrowMarkerType;
  arrowEnd?: ArrowMarkerType;
}

export function buildStyleSubmenus(
  data: ConnectorStyleData,
  connectorId: string,
  store: CanvasStore,
  updateStyle: (changes: Record<string, unknown>) => void,
): ContextMenuItem[] {
  const currentColor = data.strokeColor ?? DEFAULT_EDGE_COLOR;
  const currentWidth = data.strokeWidth ?? DEFAULT_EDGE_WIDTH;
  const currentStyle: StrokeStyle = data.strokeStyle ?? 'solid';
  const currentArrowStart: ArrowMarkerType = data.arrowStart ?? 'none';
  const currentArrowEnd: ArrowMarkerType = data.arrowEnd ?? (data.segmentType === 'bezier' || data.segmentType === 'straight' ? 'arrow' : 'none');

  const typeLabels: Record<SegmentType, string> = { bezier: 'Bezier', straight: t('canvasMenu.segmentStraight'), ortho90: 'L', angle45: '45\u00B0', elbowed: '90\u00B0' };
  const typeItems: ContextMenuItem[] = (['bezier', 'straight', 'ortho90', 'angle45', 'elbowed'] as SegmentType[]).map(t => ({
    id: `type-${t}`,
    label: `${data.segmentType === t ? '\u25CF ' : ''}${typeLabels[t]}`,
    onClick: () => store.send({ type: 'connector.setType', connectorId, segmentType: t }),
  }));

  // Color: swatch grid with EDGE_COLORS + Tailwind palette
  const colorSubmenu = buildColorGridSubmenu({
    id: 'line-color',
    label: t('canvasMenu.lineColor'),
    currentColor,
    handlerColors: [...EDGE_COLORS],
    onSelectColor: (color) => updateStyle({ strokeColor: color }),
  });

  const styleItems: ContextMenuItem[] = STROKE_STYLES.map(s => ({
    id: `style-${s.id}`,
    label: `${currentStyle === s.id ? '\u25CF ' : ''}${s.label}`,
    onClick: () => updateStyle({ strokeStyle: s.id }),
  }));

  const widthItems: ContextMenuItem[] = STROKE_WIDTHS.map(w => ({
    id: `width-${w}`,
    label: `${currentWidth === w ? '\u25CF ' : ''}${w}px`,
    onClick: () => updateStyle({ strokeWidth: w }),
  }));

  const arrowStartItems: ContextMenuItem[] = ARROW_TYPES.map(a => ({
    id: `arrowstart-${a.id}`,
    label: `${currentArrowStart === a.id ? '\u25CF ' : ''}${a.label}`,
    onClick: () => updateStyle({ arrowStart: a.id }),
  }));

  const arrowEndItems: ContextMenuItem[] = ARROW_TYPES.map(a => ({
    id: `arrowend-${a.id}`,
    label: `${currentArrowEnd === a.id ? '\u25CF ' : ''}${a.label}`,
    onClick: () => updateStyle({ arrowEnd: a.id }),
  }));

  return [
    { id: 'line-type', label: t('canvasMenu.lineType'), children: typeItems },
    colorSubmenu,
    { id: 'line-style', label: t('canvasMenu.lineStyle'), children: styleItems },
    { id: 'line-width', label: t('canvasMenu.lineWidth'), children: widthItems },
    { id: 'arrow-start', label: t('canvasMenu.arrowStart'), separator: true, children: arrowStartItems },
    { id: 'arrow-end', label: t('canvasMenu.arrowEnd'), children: arrowEndItems },
  ];
}

export function buildLabelItems(
  label: string | undefined,
  updateStyle: (changes: Record<string, unknown>) => void,
): ContextMenuItem[] {
  if (label) {
    return [
      {
        id: 'edit-label',
        label: t('canvasMenu.editLabel'),
        icon: Edit3,
        separator: true,
        onClick: () => {
          const newLabel = window.prompt(t('canvasMenu.labelPrompt'), label);
          if (newLabel !== null) {
            updateStyle({ label: newLabel || undefined });
          }
        },
      },
      {
        id: 'remove-label',
        label: t('canvasMenu.removeLabel'),
        onClick: () => updateStyle({ label: undefined }),
      },
    ];
  }

  return [
    {
      id: 'add-label',
      label: t('canvasMenu.addLabel'),
      icon: Edit3,
      separator: true,
      onClick: () => {
        const newLabel = window.prompt(t('canvasMenu.labelPrompt'));
        if (newLabel) {
          updateStyle({ label: newLabel });
        }
      },
    },
  ];
}

// ============================================
// High-level wrappers for Entity-based connectors
// ============================================

export function buildConnectorStyleSubmenus(
  entity: CanvasEntity,
  store: CanvasStore,
): ContextMenuItem[] {
  const cd = getConnectorData(entity);
  const updateStyle = (changes: Record<string, unknown>) => {
    store.send({ type: 'connector.updateStyle', connectorId: entity.id, changes });
  };
  return buildStyleSubmenus(cd, entity.id, store, updateStyle);
}

export function buildConnectorLabelItems(
  entity: CanvasEntity,
  store: CanvasStore,
): ContextMenuItem[] {
  const cd = getConnectorData(entity);
  const updateStyle = (changes: Record<string, unknown>) => {
    store.send({ type: 'connector.updateStyle', connectorId: entity.id, changes });
  };
  return buildLabelItems(cd.label, updateStyle);
}
